import React, { useEffect, useCallback, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { clearCart } from '../redux/slices/cartSlice';
import { PageBackButton } from '../components';
import { useCheckoutForm, OrderType } from '../hooks/useCheckoutForm';
import { useOrderCalculations } from '../hooks/useOrderCalculations';
import { useOrderManagement } from '../hooks/useOrderManagement';
import { useAppNavigate } from '../hooks/useAppNavigate';
import { PaymentPreferences } from '../types/restaurant';
import {
  CheckoutForm,
  OrderTypeTabs,
  OrderSummary,
  EmptyCart,
  TakeoutSuccessPopup,
  PaymentSuccessPopup,
  PaymentFailedPopup,
  PaymentFailedProcessingPopup,
  DeliveryQuoteModal,
  DeliverySuccessPopup,
} from '../components/checkout';

const CheckoutPage: React.FC = () => {
  const appNavigate = useAppNavigate();
  const { items: cartItems } = useAppSelector(state => state.cart);
  const { selectedRestaurant } = useAppSelector(state => state.restaurant);
  const dispatch = useAppDispatch();
  
  // Custom hooks
  const {
    activeTab,
    setActiveTab,
    formData,
    errors,
    handleChange,
    validateForm,
  } = useCheckoutForm();
  
  const { subtotal, deliveryFee, tax, total } = useOrderCalculations(cartItems, activeTab);
  
  const {
    submittingMethod,
    showTakeoutSuccessPopup,
    showPaymentFailedPopup,
    showPaymentFailedProcessingPopup,
    showPaymentSuccessPopup,
    showDeliveryQuoteModal,
    showDeliverySuccessPopup,
    orderLoading,
    orderResponse,
    deliveryQuoteLoading,
    savedDeliveryQuote,
    handleTakeoutOrder,
    handlePayNowOrder,
    handleDeliveryQuote,
    handlePlaceDeliveryOrder,
    handleContinueShopping,
    handlePaymentSuccess,
    handlePaymentRetry,
    resetAllPopupStates,
    initiatePayment,
    setShowDeliveryQuoteModal,
    setShowDeliverySuccessPopup,
  } = useOrderManagement();

  // Parse payment preferences to control button and tab visibility
  const { showPayNow, showPayLater, isDeliveryEnabled } = useMemo(() => {
    let paymentPrefs: PaymentPreferences | null = null;
    
    if (selectedRestaurant?.payment_perferences) {
      try {
        paymentPrefs = typeof selectedRestaurant.payment_perferences === 'string'
          ? JSON.parse(selectedRestaurant.payment_perferences)
          : selectedRestaurant.payment_perferences;
      } catch (error) {
        console.error('Failed to parse payment_perferences:', error);
      }
    }
    
    // Default to true if keys don't exist (backward compatible)
    // isDeliveryEnabled defaults to false (delivery must be explicitly enabled)
    return {
      showPayNow: paymentPrefs?.paynow ?? true,
      showPayLater: paymentPrefs?.paylater ?? true,
      isDeliveryEnabled: paymentPrefs?.isDeliveryEnabled ?? false,
    };
  }, [selectedRestaurant?.payment_perferences]);

  // Auto-reset to pickup if delivery is disabled but somehow selected
  useEffect(() => {
    if (!isDeliveryEnabled && activeTab === 'delivery') {
      setActiveTab('pickup');
    }
  }, [isDeliveryEnabled, activeTab, setActiveTab]);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  const handleFormSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      handlePayNowOrder(formData, total, activeTab);
    }
  }, [validateForm, handlePayNowOrder, formData, total, activeTab]);

  const handleTakeoutSubmit = useCallback(() => {
    if (validateForm()) {
      handleTakeoutOrder(formData, total, activeTab);
    }
  }, [validateForm, handleTakeoutOrder, formData, total, activeTab]);

  const handleDeliveryQuoteSubmit = useCallback(() => {
    if (validateForm()) {
      handleDeliveryQuote(formData, total, deliveryFee);
    }
  }, [validateForm, handleDeliveryQuote, formData, total, deliveryFee]);

  // Show empty cart if no items
  if (cartItems.length === 0) {
    return <EmptyCart />;
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 flex flex-col">
      {/* Checkout Content */}
      <div className="container mx-auto py-12 px-4">
        <PageBackButton text="Menu" onClick={() => appNavigate('/menu')} />
        <div className="mb-8">
          <h1 className="text-4xl font-bold">Checkout</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            {/* Tabs Outside Form */}
            {isDeliveryEnabled && (
              <div className="mb-4">
                <OrderTypeTabs 
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  isDeliveryEnabled={isDeliveryEnabled}
                />
              </div>
            )}
            
            <CheckoutForm
              formData={formData}
              errors={errors}
              handleChange={handleChange}
              onSubmit={handleFormSubmit}
              onTakeoutOrder={handleTakeoutSubmit}
              onDeliveryQuote={handleDeliveryQuoteSubmit}
              orderLoading={orderLoading || deliveryQuoteLoading}
              submittingMethod={submittingMethod}
              cartItemsCount={cartItems.length}
              showPayNow={showPayNow}
              showPayLater={showPayLater}
              activeTab={activeTab}
            />
          </div>

          {/* Order Summary */}
          <div>
            <OrderSummary
              cartItems={cartItems}
              orderType={activeTab}
              subtotal={subtotal}
              deliveryFee={deliveryFee}
              tax={tax}
              total={total}
              savedDeliveryQuote={savedDeliveryQuote}
            />
          </div>
        </div>
      </div>

      {/* Takeout Success Popup */}
      <TakeoutSuccessPopup
        isOpen={showTakeoutSuccessPopup}
        onContinue={handleContinueShopping}
      />

      {/* Payment Popups */}
      <PaymentSuccessPopup
        isOpen={showPaymentSuccessPopup}
        onClose={() => resetAllPopupStates()}
        onContinue={() => handlePaymentSuccess(() => {
          dispatch(clearCart());
          // Navigate to menu after successful payment
          appNavigate('/menu');
        })}
      />

      <PaymentFailedPopup
        isOpen={showPaymentFailedPopup}
        onClose={() => resetAllPopupStates()}
        onTryAgain={() => handlePaymentRetry(() => {
          // Retry the payment with the same payment link
          if (orderResponse?.payment_link) {
            initiatePayment(orderResponse.payment_link);
          }
        })}
      />

      <PaymentFailedProcessingPopup
        isOpen={showPaymentFailedProcessingPopup}
        onClose={() => resetAllPopupStates()}
        onTryAgain={() => handlePaymentRetry(() => {
          // Retry the payment with the same payment link
          if (orderResponse?.payment_link) {
            initiatePayment(orderResponse.payment_link);
          }
        })}
      />

      {/* Delivery Quote Modal */}
      <DeliveryQuoteModal
        isOpen={showDeliveryQuoteModal}
        cartItems={cartItems}
        deliveryFee={savedDeliveryQuote?.deliveryChargeCents || 0}
        subtotal={subtotal}
        total={subtotal + (savedDeliveryQuote?.deliveryChargeCents || 0)}
        onPlaceOrder={handlePlaceDeliveryOrder}
        onClose={() => setShowDeliveryQuoteModal(false)}
        loading={orderLoading}
      />

      {/* Delivery Success Popup */}
      <DeliverySuccessPopup
        isOpen={showDeliverySuccessPopup}
        onContinue={() => {
          setShowDeliverySuccessPopup(false);
          dispatch(clearCart());
          appNavigate('/menu');
        }}
      />
    </div>
  );
};

export default CheckoutPage;
